import { useCallback, useEffect, useState } from "react";
import { DeleteTwoTone, EditOutlined, EyeOutlined, MoreOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import moment from "moment";
import { Link, useLocation, useNavigate } from "react-router-dom";
import AppContent from "common/AppContent";
import AppPageHeader from "common/PageHeader";
import { deleteData, getData } from "apiServices/common";
import {
  MER_SAMPLE_REQUISITION_BUYER_DATA,
  MER_SAMPLE_REQUISITION_DELETE,
  MER_SAMPLE_REQUISITION_LIST,
  MER_SAMPLE_REQUISITION_SAMPLE_TYPES,
} from "apiServices/API_ENDPOINTS";
import {
  SAMPLE_REQUISITION_CREATE_PERMISSION,
  SAMPLE_REQUISITION_DELETE_PERMISSION,
  SAMPLE_REQUISITION_EDIT_PERMISSION,
  SAMPLE_REQUISITION_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

const { Title } = Typography;
const { RangePicker } = DatePicker;

const BASE_PATH = "/merchandising/sample-requisition";
const DISPLAY_DATE_FORMAT = "DD-MMM-YY";

const formatDate = (date) => (date ? moment(date).format(DISPLAY_DATE_FORMAT) : "");

const readFilterStatus = () => {
  try {
    return JSON.parse(localStorage.getItem("filter_status") || "{}");
  } catch {
    return {};
  }
};

const SampleRequisition = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [filterForm] = Form.useForm();

  const [showFilter, setShowFilter] = useState(() => !!readFilterStatus()[pathname]);

  useEffect(() => {
    localStorage.setItem("filter_status", JSON.stringify({ ...readFilterStatus(), [pathname]: showFilter }));
  }, [showFilter, pathname]);

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ pageSize: 10, total: 0 });
  const [filters, setFilters] = useState({});
  const [buyers, setBuyers] = useState([]);
  const [sampleTypes, setSampleTypes] = useState([]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      const [buyerResponse, sampleTypeResponse] = await Promise.all([
        getData(MER_SAMPLE_REQUISITION_BUYER_DATA),
        getData(MER_SAMPLE_REQUISITION_SAMPLE_TYPES),
      ]);
      if (buyerResponse?.data?.code === 200) setBuyers(buyerResponse.data.data ?? []);
      if (sampleTypeResponse?.data?.code === 200) setSampleTypes(sampleTypeResponse.data.data ?? []);
    };
    loadFilterOptions();
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    const response = await getData(`${MER_SAMPLE_REQUISITION_LIST}?page=${currentPage}`, false, filters);
    setLoading(false);

    if (response?.data?.code === 200) {
      const paginated = response.data.data;
      setTableData(paginated?.data ?? []);
      setPagination({ total: paginated?.total ?? 0, pageSize: paginated?.per_page ?? 10 });
    }
  }, [currentPage, filters]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSearch = (values) => {
    const [fromDate, toDate] = values.date_range ?? [];
    setFilters({
      ...(values.reference_no && { reference_no: values.reference_no }),
      ...(values.buyer_id && { buyer_id: values.buyer_id }),
      ...(values.sample_type_id && { sample_type_id: values.sample_type_id }),
      ...(fromDate && { from_date: fromDate.format("YYYY-MM-DD") }),
      ...(toDate && { to_date: toDate.format("YYYY-MM-DD") }),
    });
    setCurrentPage(1);
  };

  const onReset = () => {
    filterForm.resetFields();
    setFilters({});
    setCurrentPage(1);
  };

  const onDelete = async (id) => {
    const response = await deleteData(MER_SAMPLE_REQUISITION_DELETE(id));
    if (response?.code === 200) {
      message.success("Sample Requisition deleted successfully");
      fetchList();
    }
  };

  const actionMenu = (record) => (
    <Menu
      items={[
        {
          key: "view",
          label: (
            <Link to={`${BASE_PATH}/view/${record.id}`}>
              <EyeOutlined /> View
            </Link>
          ),
          disabled: !hasPermission([SAMPLE_REQUISITION_VIEW_PERMISSION]),
        },
        {
          key: "edit",
          label: (
            <Link to={`${BASE_PATH}/edit/${record.id}`}>
              <EditOutlined /> Edit
            </Link>
          ),
          disabled: !hasPermission([SAMPLE_REQUISITION_EDIT_PERMISSION]),
        },
        {
          key: "delete",
          label: (
            <Popconfirm title="Are you sure you want to delete?" onConfirm={() => onDelete(record.id)}>
              <span style={{ color: "red" }}>
                <DeleteTwoTone twoToneColor="red" /> Delete
              </span>
            </Popconfirm>
          ),
          disabled: !hasPermission([SAMPLE_REQUISITION_DELETE_PERMISSION]),
        },
      ]}
    />
  );

  const columns = [
    { title: "Sample Reference", dataIndex: "reference_no", key: "reference_no" },
    { title: "Date", dataIndex: "requisition_date", key: "requisition_date", render: formatDate },
    { title: "Buyer", dataIndex: ["buyer", "name"], key: "buyer" },
    { title: "Buying Office", dataIndex: ["buying_office", "name"], key: "buying_office" },
    {
      title: "Style",
      dataIndex: "styles",
      key: "styles",
      render: (styles) => (styles ?? []).map((style) => style.style_no).join(", "),
    },
    {
      title: "Sub Style",
      dataIndex: "sub_styles",
      key: "sub_styles",
      render: (subStyles) => (subStyles ?? []).map((subStyle) => subStyle.sub_style_no).join(", "),
    },
    { title: "Type of Sample", dataIndex: ["sample_type", "name"], key: "sample_type" },
    { title: "Sample Qnty", dataIndex: "sample_qty", key: "sample_qty", align: "right" },
    {
      title: "Requirement Date",
      dataIndex: "sample_requirement_date",
      key: "sample_requirement_date",
      render: formatDate,
    },
    {
      title: "Expected Submission",
      dataIndex: "expected_submission_date",
      key: "expected_submission_date",
      render: formatDate,
    },
    { title: "Created By", dataIndex: ["created_by", "name"], key: "created_by" },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Dropdown overlay={actionMenu(record)}>
          <MoreOutlined style={{ fontSize: "20px", cursor: "pointer" }} />
        </Dropdown>
      ),
    },
  ];

  const canCreate = hasPermission([SAMPLE_REQUISITION_CREATE_PERMISSION]);

  return (
    <AppContent
      header={
        <AppPageHeader
          title={<Title level={5}>Sample Requisition</Title>}
          extra={
            <Space size={8}>
              <Button size="small" type={showFilter ? "danger" : "primary"} onClick={() => setShowFilter((prev) => !prev)}>
                {`${showFilter ? "Close" : "Open"} Filter Option`}
              </Button>
              <Button size="small" type="primary" disabled={!canCreate} onClick={() => navigate(`${BASE_PATH}/create`)}>
                {canCreate ? "Add New" : "No Permission"}
              </Button>
            </Space>
          }
        />
      }
      content={
        <div style={{ padding: 16 }}>
          {showFilter && (
            <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: "12px 16px 0" }}>
              <Form form={filterForm} layout="vertical" onFinish={onSearch}>
                <Row gutter={16} align="bottom" wrap>
                  <Col flex="1 1 180px">
                    <Form.Item label="Sample Reference" name="reference_no">
                      <Input allowClear placeholder="CGL-SAMPLE-..." />
                    </Form.Item>
                  </Col>
                  <Col flex="1 1 180px">
                    <Form.Item label="Buyer" name="buyer_id">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="All buyers"
                        options={buyers.map((buyer) => ({ label: buyer.name, value: buyer.id }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="1 1 160px">
                    <Form.Item label="Type of Sample" name="sample_type_id">
                      <Select
                        allowClear
                        placeholder="All types"
                        options={sampleTypes.map((type) => ({ label: type.name, value: type.id }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col flex="1 1 240px">
                    <Form.Item label="Date" name="date_range">
                      <RangePicker style={{ width: "100%" }} format="DD-MMM-YYYY" />
                    </Form.Item>
                  </Col>
                  <Col flex="none">
                    <Form.Item>
                      <Space>
                        <Button type="primary" htmlType="submit">
                          Search
                        </Button>
                        <Button onClick={onReset}>
                          Reset
                        </Button>
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </Card>
          )}
          <Table
            bordered
            size="small"
            rowKey="id"
            loading={loading}
            dataSource={tableData}
            columns={columns}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              pageSize: pagination.pageSize,
              total: pagination.total,
              onChange: setCurrentPage,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            }}
          />
        </div>
      }
    />
  );
};

export default SampleRequisition;
